import { APIController } from '../api/controller';
import { UserModel } from '../user/model';
import { BugsnagController } from '../bugsnag/controller';
import { EnvModel } from '../env/model';
import { action, autorun, IReactionDisposer, observable, runInAction } from 'mobx';
import { ArtistlistModel } from './model';
import { PageController } from '../page/controller';
import { ClientController } from '../client/controller';

export class ArtistlistController {
  constructor(
    private model: ArtistlistModel,
    private env: EnvModel,
    private user: UserModel,
    private client: ClientController,
    private api: APIController,
    private bugsnag: BugsnagController,
  ) {
    this._disposers = [];

    this.model.sync[this.env.artistlistFavourites] = observable([]);
    this.model.artists[this.env.artistlistFavourites] = observable([]);
    this.model.removed[this.env.artistlistFavourites] = observable([]);

    if (!this.env.isServer) {
      let userId: string;
      this._disposers.push(
        autorun(() => {
          if (user.user && user.user.identity && user.user.identity !== userId) {
            this.fetchFavourites();
            userId = user.user.identity;
          }
        }),
      );
    }
  }

  private _disposers: IReactionDisposer[];

  dispose = () => {
    this._disposers.forEach((dispose) => dispose());
  };

  @action
  addToArtistlist = async (artistlist: string, artist: string) => {
    if (!this.user.user) return;

    this.setSync(artistlist, artist, true);

    try {
      await this.api.artistlist.addArtistToArtistlist(this.user.user.identity, artistlist, artist);

      this.setActive(artistlist, artist, true);
    } catch (e) {
      this.bugsnag.notifyException(e);
    } finally {
      this.setSync(artistlist, artist, false);
    }
  };

  @action
  removeFromArtistlist = async (artistlist: string, artist: string) => {
    if (!this.user.user) return;

    this.setSync(artistlist, artist, true);

    try {
      await this.api.artistlist.removeArtistFromArtistlist(this.user.user.identity, artistlist, artist);

      this.setActive(artistlist, artist, false);
    } catch (e) {
      this.bugsnag.notifyException(e);
    } finally {
      this.setSync(artistlist, artist, false);
    }
  };

  @action
  favourite = async (artist: string) => {
    await this.addToArtistlist(this.env.artistlistFavourites, artist);
    // Reload favourite lists on homepage, don't await
    this.client.page.home.loadUserContent();
  };

  @action
  unfavourite = async (artist: string) => {
    await this.removeFromArtistlist(this.env.artistlistFavourites, artist);
    // Reload favourite lists on homepage, don't await
    this.client.page.home.loadUserContent();
  };

  @action
  fetchFavourites = async () => {
    if (!this.user.user) return;

    try {
      const artists = await this.api.artistlist.getArtists(this.user.user.identity, this.env.artistlistFavourites);

      runInAction(() => {
        this.model.artists[this.env.artistlistFavourites].splice(
          0,
          this.model.artists[this.env.artistlistFavourites].length,
          ...artists.data.map((t) => t.identity),
        );
      });
    } catch (e) {
      if (e.status && e.status === 404) {
        // we expect a 404 for empty lists
        runInAction(() => {
          this.model.artists[this.env.artistlistFavourites].splice(
            0,
            this.model.artists[this.env.artistlistFavourites].length,
          );
        });
      } else {
        this.bugsnag.notifyException(e);
      }
    }
  };

  @action
  private setSync = (artistlist: string, artist: string, sync: boolean) => {
    if (!this.model.sync[artistlist]) {
      this.model.sync[artistlist] = observable([]);
    }

    if (sync) {
      this.model.sync[artistlist].push(artist);
    } else {
      const index = this.model.sync[artistlist].indexOf(artist);

      if (index !== -1) {
        this.model.sync[artistlist].splice(index, 1);
      }
    }
  };

  @action
  private setActive = (artistlist: string, artist: string, active: boolean) => {
    if (!this.model.artists[artistlist]) {
      this.model.artists[artistlist] = observable([]);
    }

    if (!this.model.removed[artistlist]) {
      this.model.removed[artistlist] = observable([]);
    }

    if (active) {
      this.model.artists[artistlist].push(artist);

      const index = this.model.removed[artistlist].indexOf(artist);

      if (index !== -1) {
        this.model.removed[artistlist].splice(index, 1);
      }
    } else {
      this.model.removed[artistlist].push(artist);

      const index = this.model.artists[artistlist].indexOf(artist);

      if (index !== -1) {
        this.model.artists[artistlist].splice(index, 1);
      }
    }
  };
}
